import type { ReactNode } from "react";
import { Check, ChevronDown, Download, Info } from "lucide-react";

import { cn } from "@/lib/utils";

export type ObligationState = "urgent" | "pending" | "declared";

interface ObligationCardProps {
  state: ObligationState;
  dateDay: string;
  dateMonth: string;
  name: ReactNode;
  nameSerif?: ReactNode;
  /**
   * Pill rendered right after the name (and before the tag) to flag a
   * regime that materially changes how the tax is computed — currently
   * used for IVA Mínimo so Literal E tenants can tell at a glance their
   * "IVA a pagar" comes from the tiered flat rule, not debit-credit.
   */
  nameBadge?: ReactNode;
  tag: string;
  description: ReactNode;
  amount?: { label: string; value: string };
  amountKind?: "credit" | "debit";
  /** Primary action button copy (e.g. "Marcar declarado"). Null hides the button. */
  actionLabel?: string | null;
  onAction?: () => void;
  actionDisabled?: boolean;
  /** Secondary action rendered alongside the primary (e.g. "Descargar boleto"). Hidden when null/absent. */
  secondaryActionLabel?: string | null;
  onSecondaryAction?: () => void;
  secondaryActionDisabled?: boolean;
  expanded: boolean;
  onToggle: () => void;
  children?: ReactNode;
}

export function ObligationCard({
  state,
  dateDay,
  dateMonth,
  name,
  nameSerif,
  nameBadge,
  tag,
  description,
  amount,
  amountKind,
  actionLabel,
  onAction,
  actionDisabled,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionDisabled,
  expanded,
  onToggle,
  children,
}: ObligationCardProps): JSX.Element {
  const cls = ["ef-ob"];
  if (state === "urgent") cls.push("urgent");
  if (state === "declared") cls.push("declared");
  if (expanded) cls.push("expanded");

  return (
    <div className={cls.join(" ")}>
      <div className="ef-ob-head">
        <div className="date">
          <span className="d">{dateDay}</span>
          <span className="m">{dateMonth}</span>
        </div>
        <div className="ob-body">
          <div className="ob-title">
            <span className="name">
              {name}
              {nameSerif ? (
                <>
                  {" "}
                  <span className="serif">{nameSerif}</span>
                </>
              ) : null}
            </span>
            {nameBadge ? <span className="ob-name-badge">{nameBadge}</span> : null}
            {tag ? <span className="ob-tag">{tag}</span> : null}
          </div>
          <div className="ob-desc">{description}</div>
        </div>
        <div className="ob-actions">
          {amount ? (
            <div className="ob-amount">
              <div className="lbl">{amount.label}</div>
              <div className={cn("val", amountKind === "credit" && "credit")}>
                {amount.value}
              </div>
            </div>
          ) : null}
          {secondaryActionLabel && onSecondaryAction ? (
            <button
              type="button"
              onClick={onSecondaryAction}
              disabled={secondaryActionDisabled}
              className="fx-btn fx-btn-sm fx-btn-ghost"
            >
              <Download className="h-3.5 w-3.5" aria-hidden />
              {secondaryActionLabel}
            </button>
          ) : null}
          {actionLabel && onAction ? (
            <button
              type="button"
              onClick={onAction}
              disabled={actionDisabled}
              className={cn(
                "fx-btn fx-btn-sm",
                state === "urgent" ? "fx-btn-accent" : "fx-btn-ghost"
              )}
            >
              <Check className="h-3.5 w-3.5" aria-hidden />
              {actionLabel}
            </button>
          ) : null}
          {children ? (
            <button
              type="button"
              className="ob-toggle"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-label={expanded ? "Cerrar detalle" : "Ver detalle"}
            >
              <ChevronDown />
            </button>
          ) : null}
        </div>
      </div>
      <div className="ef-ob-detail">{children}</div>
    </div>
  );
}

interface BalanceEquationProps {
  ventas: { label: string; value: string };
  compras: { label: string; value: string };
  periodo: { label: string; value: string; credit?: boolean };
  total: { label: string; value: string; credit?: boolean };
  currency?: string;
}

/**
 * Equation-style horizontal strip: A − B = C ⟶ D.
 * The terminal cell inverts to ink-900 with paper text; credit values colored olive.
 */
export function BalanceEquation({
  ventas,
  compras,
  periodo,
  total,
  currency = "UYU",
}: BalanceEquationProps): JSX.Element {
  return (
    <div className="ef-balance">
      <div className="ef-bal-cell">
        <div className="k">{ventas.label}</div>
        <div className="v">
          <span>{ventas.value}</span>
          <span className="u">{currency}</span>
        </div>
      </div>
      <div className="ef-bal-op">−</div>
      <div className="ef-bal-cell">
        <div className="k">{compras.label}</div>
        <div className="v">
          <span>{compras.value}</span>
          <span className="u">{currency}</span>
        </div>
      </div>
      <div className="ef-bal-op">=</div>
      <div className={cn("ef-bal-cell", periodo.credit && "credit")}>
        <div className="k">{periodo.label}</div>
        <div className="v">
          <span>{periodo.value}</span>
          <span className="u">{currency}</span>
        </div>
      </div>
      <div className="ef-bal-op equals">⟶</div>
      <div className={cn("ef-bal-cell total", total.credit && "credit")}>
        <div className="k">
          <Info className="h-3 w-3" aria-hidden />
          {total.label}
        </div>
        <div className={cn("v", total.credit && "credit")}>
          <span>{total.value}</span>
          <span className="u">{currency}</span>
        </div>
      </div>
    </div>
  );
}

interface ObligationDetailFootProps {
  note: ReactNode;
  exportLabel?: string;
  onExport?: () => void;
}

export function ObligationDetailFoot({
  note,
  exportLabel,
  onExport,
}: ObligationDetailFootProps): JSX.Element {
  return (
    <div className="ef-detail-foot">
      <div className="note">{note}</div>
      {exportLabel && onExport ? (
        <a
          onClick={(e) => {
            e.preventDefault();
            onExport();
          }}
        >
          <Download className="h-3 w-3" aria-hidden />
          {exportLabel}
        </a>
      ) : null}
    </div>
  );
}

interface SectionHeadProps {
  title: ReactNode;
  titleSerif?: ReactNode;
  countLabel?: ReactNode;
}

export function ObligacionesSectionHead({
  title,
  titleSerif,
  countLabel,
}: SectionHeadProps): JSX.Element {
  return (
    <div className="ef-section-head">
      <div className="title">
        {title}
        {titleSerif ? (
          <>
            {" "}
            <span className="serif">{titleSerif}</span>
          </>
        ) : null}
      </div>
      {countLabel ? (
        <div className="count">
          <span className="dot" />
          {countLabel}
        </div>
      ) : null}
    </div>
  );
}
